import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {NgxPopupComponent} from '../../ngx-popups/ngx-popups/components/popup.component';
import {AutoUnsubscribe} from '@decorators/auto-unsubscribe';
import {Company} from "@models/company";
import { FileItem, FileUploader, ParsedResponseHeaders } from '@components/shared/file-upload';
import {Subscription} from "rxjs";
import {AlertService} from "@services/alert.service";
import {Router} from "@angular/router";
import {DocumentFileCreateRequest} from "@models/document-file-create-request";
import {DocumentFileCreateRequestState} from "@utils/enums";
import {DocumentFileService} from "@services/document-file.service";
import {ReviewQueueService} from "@services/review-queue.service";
import {SUPPORTED_TAX_FORMS, TAX_RETURN} from "@utils/constants";
import {CustomAttributeDefinition} from "@models/custom-attribute-definition";
import {CustomAttributeDefinitionService} from "@services/custom-attribute-definition.service";
import {LaunchDarklyService} from "@services/launchdarkly.service";
import {BankSettingsService} from "@services/bank-settings.service";
import {Select2OptionData} from "@components/shared/select2/select2.interface";
import {finalize} from "rxjs/operators";
import {
  FileItemRowComponent
} from "@components/shared/popups/upload-document-modal/file-item-row/file-item-row.component";
import { PDFDocument } from 'pdf-lib';

declare var bootstrap: any;

const FILE_UPLOAD_LIMIT = 10;
const MIN_COMBINE_DOCUMENT_COUNT = 2;
const MIN_COMBINE_DOCUMENT_ERROR = 'Please select two or more documents to combine.';
export const MAX_FILE_NAME_LENGTH = 200;
export const FILE_NAME_TOO_LONG_ERROR = 'One or more file names exceeds the 200-character limit. Please shorten the file name(s) and try again.';

const allowedMimeTypes = [
  'image/x-png',
  'image/png',
  'image/gif',
  'image/jpeg',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel'
];

export class NgtFileItem {
  // Wrapper for the uploader FileItem type, allows us to attach user-inputted info about the upload and form error
  // state to the file object.
  fileItem: FileItem;
  selectedType: string = '';
  selectedTaxForm: string = '';
  selectedStatementDate: string = '';
  customAttributesValues: any = {};
  combine: boolean = false;
  combineOrder?: number; // Track the order in which files were checked for combination
  status?: 'uploaded' | 'submitting' | 'submission_complete' | 'error' = 'uploaded'
  error = {
    'selectedType': false,
    'selectedTaxForm': false,
    'selectedStatementDate': false,
    'invalidCustomAttributeFields': {}
  }

  constructor(fileItem: FileItem) {
    this.fileItem = fileItem;
  }
}


@Component({
    selector: 'app-upload-document-modal',
    templateUrl: './upload-document-modal.component.html',
    styleUrls: ['./upload-document-modal.component.scss'],
    standalone: false
})
@AutoUnsubscribe('subsArr$')
export class UploadDocumentModalComponent implements OnInit, AfterViewInit {
  @Input() popup: NgxPopupComponent;
  @Input() company: Company;
  @ViewChild('uploadInput', {static: true}) uploadInputHtml: ElementRef;
  @ViewChildren('uploadedDocumentRows') uploadedDocumentRows: QueryList<FileItemRowComponent>;

  subsArr$: Subscription[] = [];
  uploader: FileUploader;
  uploadedFiles: NgtFileItem[] = [];
  fileDragOver = false;
  fileUploadProcessBegun = false;
  FILE_UPLOAD_LIMIT = FILE_UPLOAD_LIMIT;

  customAttrDefinitions: CustomAttributeDefinition[] = [];
  SUPPORTED_TAX_FORMS: Array<string> = Array.from(SUPPORTED_TAX_FORMS);
  showPuertoRicoCorporate: Boolean = false;
  showPuertoRicoIndividual: Boolean = false;
  showDocumentUploadWarning = false;
  documentUploadWarningText = '';
  taxFormOptions: Select2OptionData[];

  fileNotSelectedError = false;
  uploadInputAccept = allowedMimeTypes.join();
  
  // Combination summary properties
  combinedFileName: string = '';
  combinedFileExtension: string = '';
  combinationErrors: string[] = [];
  private nextCombineOrder: number = 1;
  showCombineDocumentFeature: boolean = false;
  private userHasCustomizedFileName: boolean = false; 

  constructor(
    private _alertService: AlertService,
    private _router: Router,
    private documentFileService: DocumentFileService,
    private _reviewQueueService: ReviewQueueService,
    private _customAttributeDefinitionService: CustomAttributeDefinitionService,
    private _featureFlags: LaunchDarklyService,
    private changeDetectorRef: ChangeDetectorRef,
    private _bankSettingsService: BankSettingsService,
  ) {
  }

  
  ngOnInit() {
    this.buildUploader();
    this.updateFlags();
    this.updateSupportedForms();
    this.subsArr$.push(this._featureFlags.flagChange.subscribe(() => {
      this.updateFlags();
      this.updateSupportedForms();
    }));

    this.subsArr$.push(this._customAttributeDefinitionService.getCustomAttributesForModel('DocumentFile').subscribe(customAttributeDefinitions => {
      this.customAttrDefinitions = customAttributeDefinitions;
    }));

    this.subsArr$.push(this._bankSettingsService.getDocumentUploadWarningMessage().subscribe(message => {
      this.showDocumentUploadWarning = !!message;
      if (message) {
        this.documentUploadWarningText = message;
      }
    }));
  }

  ngAfterViewInit() {
    this.enableTooltips()
  }

  enableTooltips() {
    // Bootstrap tooltip initialization
    const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
    tooltipTriggerList.forEach(tooltipTriggerEl => {
      return new bootstrap.Tooltip(tooltipTriggerEl)
    })
  }

  buildUploader() {
    this.uploader = new FileUploader({
      autoUpload: false,
      method: 'PUT',
      queueLimit: 10,
      disableMultipart: true,
      isHTML5: true,
      allowedMimeType: allowedMimeTypes
    });

    this.uploader.onAfterAddingFile = (fileItem: FileItem) => {
      fileItem.withCredentials = false;
      this.fileNotSelectedError = false;
      this.uploadedFiles.push(new NgtFileItem(fileItem));
      this.changeDetectorRef.detectChanges();  // ensure the viewChildren ref is updated with most recently added file
      this.scrollToBottomOfDocumentsList();
    }

    this.uploader.onWhenAddingFileFailed = (item) => {
      let errorMsg = 'Invalid File Type';
      if (this.uploader.queue.length === FILE_UPLOAD_LIMIT){
        errorMsg = `Unable to add document. Only ${FILE_UPLOAD_LIMIT} can be uploaded at a time.`
      }
      this._alertService.error(errorMsg);
    }


    this.uploader.onErrorItem = (item: FileItem, response: string, status: number, headers: ParsedResponseHeaders) => {
      this.uploader.clearQueue();
      this.uploadInputHtml.nativeElement.value = '';
      const parsedResponse = JSON.parse(response);
      if (status === 401 || status === 403) {
        this._router.navigate(['/logout'], {queryParams: {returnUrl: this._router.url}});
        return;
      }
      this._alertService.error(parsedResponse.errors ? parsedResponse.message : 'Upload Error');
    };

    this.uploader.onSuccessItem = (item: FileItem, response: string, status: number, headers: ParsedResponseHeaders) => {
      this.uploadInputHtml.nativeElement.value = '';
      if (status === 401 || status === 403) {
        this._router.navigate(['/logout'], {queryParams: {returnUrl: this._router.url}});
      }
    };
  }


  openFilePicker() {
    // When user cancels and chooses same file, the popup is not created, so fix by setting value to null
    // https://html.spec.whatwg.org/multipage/input.html#dom-input-value-filename
    this.uploadInputHtml.nativeElement.value = null;
    this.uploadInputHtml.nativeElement.click();
  }

  removeFile(indexOfFileToRemove: number, fileItem: FileItem) {
    const removedFile = this.uploadedFiles[indexOfFileToRemove];
    if (removedFile.combine) {
      // Remove from combination and reorder remaining files
      this.handleCombineChange(removedFile, false);
    }
    this.uploadedFiles.splice(indexOfFileToRemove, 1);
    this.uploader.removeFromQueue(fileItem);
    this.fileUploadProcessBegun = false;
    
    const combinedFiles = this.getCombinedFiles();
    if (combinedFiles.length === 0) {
      this.combinedFileName = '';
      this.combinedFileExtension = '';
      this.combinationErrors = [];
    } else if (combinedFiles.length === 1 && this.uploadedFiles.length === 1) {
      const remainingCombinedFile = combinedFiles[0];
      remainingCombinedFile.combine = false;
      remainingCombinedFile.combineOrder = undefined;
      this.nextCombineOrder = 1;
      this.userHasCustomizedFileName = false;
      this.combinedFileName = '';
      this.combinedFileExtension = '';
      this.combinationErrors = [];
    } else {
      this.updateCombinedFileExtension();
      if (!this.userHasCustomizedFileName) {
        this.updateCombinedFileName();
      }
      this.validateCombination();
    }
  }
  
  handleCombineChange(file: NgtFileItem, isChecked: boolean) {
    if (isChecked) {
      // Add to combination
      file.combineOrder = this.nextCombineOrder++;
    } else {
      // Remove from combination
      const removedOrder = file.combineOrder;
      file.combineOrder = undefined;
      // Reorder remaining files
      const remainingFiles = this.uploadedFiles.filter(f => f.combine && f.combineOrder && f.combineOrder > removedOrder);
      remainingFiles.forEach(f => {
        if (f.combineOrder !== undefined) {
          f.combineOrder--;
        }
      });
      const maxOrder = this.uploadedFiles.filter(f => f.combine && f.combineOrder).map(f => f.combineOrder!);
      this.nextCombineOrder = maxOrder.length > 0 ? Math.max(...maxOrder) + 1 : 1;
    }
    
    const combinedFiles = this.getCombinedFiles();
    if (combinedFiles.length === 0) {
      this.userHasCustomizedFileName = false;
      this.combinedFileName = '';
      this.combinedFileExtension = '';
    } else {
      this.updateCombinedFileExtension();
    }
    
    if (!this.userHasCustomizedFileName) {
      this.updateCombinedFileName();
    }
    
    this.validateCombination();
    this.changeDetectorRef.detectChanges();
    // Reinitialize tooltips after view update
    setTimeout(() => this.enableTooltips(), 0);
  }
  
  getCombinedFiles(): NgtFileItem[] {
    return this.uploadedFiles
      .filter(f => f.combine)
      .sort((a, b) => (a.combineOrder || 0) - (b.combineOrder || 0));
  }
  
  
  updateCombinedFileName() {
    const combinedFiles = this.getCombinedFiles();
    if (combinedFiles.length === 0) {
      this.combinedFileName = '';
      this.combinedFileExtension = '';
      return;
    }
    
    this.updateCombinedFileExtension();
    
    // Extract file names without extensions, recursively stripping the expected extension
    const fileNamesWithoutExt = combinedFiles.map(f => {
      let fileName = f.fileItem.file.name;
      
      // Recursively strip the expected extension
      while (this.combinedFileExtension && fileName.toLowerCase().endsWith(this.combinedFileExtension.toLowerCase())) {
        fileName = fileName.substring(0, fileName.length - this.combinedFileExtension.length);
      }
      
      return fileName;
    });
    
    // Join names with "+"
    this.combinedFileName = fileNamesWithoutExt.join('+');
  }
  
  private updateCombinedFileExtension() {
    const combinedFiles = this.getCombinedFiles();
    if (combinedFiles.length === 0) {
      this.combinedFileExtension = '';
      return;
    }
    
    const firstFile = combinedFiles[0].fileItem.file;
    const fileName = firstFile.name;
    const lastDotIndex = fileName.lastIndexOf('.');
    
    if (lastDotIndex > 0 && lastDotIndex < fileName.length - 1) {
      this.combinedFileExtension = fileName.substring(lastDotIndex);
    } else {
      this.combinedFileExtension = '';
    }
  }
  
  validateCombination(showMinCombineDocumentError: boolean = false) {
    this.combinationErrors = [];
    const combinedFiles = this.getCombinedFiles();
    
    if (combinedFiles.length === 0) {
      return;
    }
    
    if (!this.combinedFileName || this.combinedFileName.trim().length === 0) {
      this.combinationErrors.push("File name cannot be empty.");
    }

    if (showMinCombineDocumentError && combinedFiles.length < MIN_COMBINE_DOCUMENT_COUNT) {
      this.combinationErrors.push(MIN_COMBINE_DOCUMENT_ERROR);
    }
    
    
    // Validate that tax returns cannot be combined
    const hasTaxReturn = combinedFiles.some(f => f.selectedType === TAX_RETURN);
    if (hasTaxReturn) {
      this.combinationErrors.push("Document combination is unavailable for tax returns at this time.");
    }
    
    // Validate file format consistency (all must be PDFs)
    const nonPdfFiles = combinedFiles.filter(f => f.fileItem.file.type !== 'application/pdf');
    if (nonPdfFiles.length > 0) {
      this.combinationErrors.push("Only PDFs can be combined. Please deselect documents until all selected items are PDFs.");
    }
    
    // Validate file name length (base name + extension must not exceed backend max_length=200)
    if (this.combinedFileName.length + this.combinedFileExtension.length > MAX_FILE_NAME_LENGTH) {
      this.combinationErrors.push("The file name exceeds 200 characters. Please rename the file.");
    }
  }

  hasFilesWithNamesTooLong(): boolean {
    return this.uploadedFiles.some(f => this.isFileNameTooLong(f));
  }

  isFileNameTooLong(file: NgtFileItem): boolean {
    return (file?.fileItem?.file?.name?.length ?? 0) > MAX_FILE_NAME_LENGTH;
  }

  fileNameTooLongError = FILE_NAME_TOO_LONG_ERROR;
  
  onCombinedFileNameChange(newName: string) {
    let strippedName = newName.trim();
    
    if (this.combinedFileExtension && strippedName.toLowerCase().endsWith(this.combinedFileExtension.toLowerCase())) {
      strippedName = strippedName.substring(0, strippedName.length - this.combinedFileExtension.length);
    }
    
    this.combinedFileName = strippedName;
    this.userHasCustomizedFileName = true;
    this.validateCombination();
  }
  
  stripFileExtensions(fileName: string): string {
    if (!fileName) {
      return '';
    }
    const lastDotIndex = fileName.lastIndexOf('.');
    if (lastDotIndex > 0) {
      const extension = fileName.substring(lastDotIndex + 1);
      // Only remove if it looks like a file extension (2-5 alphanumeric characters)
      if (/^\w{2,5}$/i.test(extension)) {
        return fileName.substring(0, lastDotIndex);
      }
    }
    return fileName;
  }
  
  hasCombinationErrors(): boolean {
    return this.combinationErrors.length > 0;
  }

  isFileNameValid(): boolean {
    return (this.combinedFileName.length + this.combinedFileExtension.length) <= MAX_FILE_NAME_LENGTH;
  }

  async mergePdfFiles(fileItems: FileItem[]): Promise<File> {

    if (fileItems.length < MIN_COMBINE_DOCUMENT_COUNT) {
      throw new Error(MIN_COMBINE_DOCUMENT_ERROR);
    }

    const pdfDoc = await PDFDocument.create();

    for (const fileItem of fileItems) {
      try {
        const fileArrayBuffer = await this.fileItemToArrayBuffer(fileItem);
        const pdf = await PDFDocument.load(fileArrayBuffer);
        const pages = await pdfDoc.copyPages(pdf, pdf.getPageIndices());
        pages.forEach((page) => pdfDoc.addPage(page));
      } catch (error) {
        let errorMessage = '';
        let errorTitle = 'Error';
        if (error.message.includes('`PDFDocument.load` is encrypted.')) {
          errorMessage = `${fileItem._file.name} can't be combined with other files as it's password protected. Please remove the password and try again.`;
          errorTitle = 'Password Protected File';
        } else if (error.message.includes('Failed to parse PDF document')) {
          errorMessage = `${fileItem._file.name} can't be parsed. Please check the file content and try again.`;
          errorTitle = 'Invalid File';
        } else {
          console.error('Error combining PDFs:', error);
          errorMessage = 'Error combining documents, please fix the errors and try again.';
        }
        this._alertService.error(errorMessage, errorTitle);
        this.combinationErrors.push(errorMessage);
        this.scrollToCombinitionSummary();
        throw error;
      }
    }
    
    const pdfBytes = await pdfDoc.save();
    const baseFileName = this.stripFileExtensions(this.combinedFileName.trim());
    const fileName = baseFileName + this.combinedFileExtension;

    return new File([pdfBytes as BlobPart], fileName, { type: 'application/pdf' });
  }

  async fileItemToArrayBuffer(fileItem: FileItem): Promise<ArrayBuffer> {
    // In ng2-file-upload with HTML5, fileItem.file should be a File object
    // But TypeScript types it as FileLikeObject, so we need to handle both cases
    const file = fileItem.file as any;
    
    // Try to use arrayBuffer if available (modern File API)
    if (file && typeof file.arrayBuffer === 'function') {
      return file.arrayBuffer();
    }
    
    // Fallback: use FileReader for older browsers or FileLikeObject
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.result instanceof ArrayBuffer) {
          resolve(reader.result);
        } else {
          reject(new Error('Failed to read file as ArrayBuffer'));
        }
      };
      reader.onerror = () => reject(reader.error || new Error('FileReader error'));
      
      const blobToRead = file instanceof Blob ? file : (fileItem as any)._file;
      if (blobToRead instanceof Blob) {
        reader.readAsArrayBuffer(blobToRead);
      } else {
        reject(new Error('Unable to read file: file is not a Blob'));
      }
    });
  }

  async processCombinedFiles(): Promise<void> {
    const combinedFiles = this.getCombinedFiles();
    
    if (combinedFiles.length === 0) {
      return;
    }
    
    try {
      const mergedFile = await this.mergePdfFiles(combinedFiles.map(f => f.fileItem));
      
      const mergedFileItem = new FileItem(this.uploader, mergedFile, {});
      // Initialize the FileItem the same way as when files are added normally
      // This ensures withCredentials and other properties are set correctly
      mergedFileItem.withCredentials = false;
      
      const mergedNgtFileItem = new NgtFileItem(mergedFileItem);
      
      // Copy properties from the first combined file (assuming they should be the same)
      const firstCombinedFile = combinedFiles[0];
      mergedNgtFileItem.selectedType = firstCombinedFile.selectedType;
      mergedNgtFileItem.selectedTaxForm = firstCombinedFile.selectedTaxForm;
      mergedNgtFileItem.selectedStatementDate = firstCombinedFile.selectedStatementDate;
      mergedNgtFileItem.customAttributesValues = { ...firstCombinedFile.customAttributesValues };
      mergedNgtFileItem.combine = false; // The merged file itself is not marked for combination
      
      // Get indices first, then remove in reverse order to avoid index shifting issues
      const indicesToRemove = combinedFiles
        .map(f => this.uploadedFiles.indexOf(f))
        .filter(index => index > -1)
        .sort((a, b) => b - a); // Sort descending to remove from end first
      
      indicesToRemove.forEach(index => {
        this.uploadedFiles.splice(index, 1);
      });
      
      combinedFiles.forEach(combinedFile => {
        this.uploader.removeFromQueue(combinedFile.fileItem);
      });
      
      this.uploadedFiles.push(mergedNgtFileItem);
      this.uploader.queue.push(mergedFileItem);
      
      // Update the combined file name to empty since we've processed it
      this.combinedFileName = '';
      this.combinedFileExtension = '';
      this.combinationErrors = [];
      this.userHasCustomizedFileName = false; 
      
      // Trigger change detection to update the UI
      this.changeDetectorRef.detectChanges();
      
    } catch (error) {
      throw error;
    }
  }

  fileOverDropZone(event: boolean) {
    if (this.fileDragOver !== event) {
      this.fileDragOver = event;
    }
  }

  async submitUploadedFiles() {
    // Block upload when any file name exceeds the backend's max_length=200 constraint
    if (this.hasFilesWithNamesTooLong()) {
      this._alertService.error(FILE_NAME_TOO_LONG_ERROR);
      return;
    }

    // Check for combination errors first
    this.validateCombination(true);
    if (this.hasCombinationErrors()) {
      if (!this.combinationErrors.includes(MIN_COMBINE_DOCUMENT_ERROR)) {
        this._alertService.error('Please fix the errors in Combination Summary');
      }
      this.scrollToCombinitionSummary();
      return;
    }
    
    this.fileUploadProcessBegun = true;
    
    const formIsValid = this.validateForm();
    if (formIsValid) {
      try {
        await this.processCombinedFiles();
      } catch (error) {
        this.fileUploadProcessBegun = false;
        return;
      }
      this.uploadedFiles.forEach(f => f.status = 'submitting');
      for (let uploadedFile of this.uploadedFiles) {
        // need to submit these synchronously so the FileUploader can process each file individually
        try {
          await this.submitFile(uploadedFile)
          uploadedFile.status ='submission_complete'
        } catch (e) {
          uploadedFile.status ='error'
        }
      }
      this._alertService.info('The documents have been uploaded and are now processing through our system.');
      if (!this._router.url.endsWith('data')) {
        this.navigateToDocumentsTab();
      }
      this.close();
    } else {
      this.fileUploadProcessBegun = false;
    }
  }

  async submitFile(uploadedFile: NgtFileItem) {
    return new Promise((resolve, reject) => {
      const docRequest = this.createDocFileRequest(uploadedFile)
      this.subsArr$.push(this.documentFileService.uploadDocumentFile(docRequest).subscribe(
        _ => {},
        e => {
          this._alertService.error('Error uploading document');
          console.error('document upload error: ', e);
          reject();
        },
        () => {
          // file successfully uploaded
          const createFilePayload = this._buildCreateFilePayload(docRequest);
          this.documentFileService.createDocumentFile(createFilePayload)
            .pipe(
              // allow the loop to continue regardless of individual file failures.
              finalize(() => resolve(true))
            )
            .subscribe(_ => {
            }, e => {
              // show error toast and proceed for individual upload failures
              this._alertService.error('Error creating document');
              console.error('document create error: ', e);
              reject();
            });
        }
      ));
    })
  }

  createDocFileRequest(uploadedFile: NgtFileItem) {
    const docFileCreateRequest = new DocumentFileCreateRequest();
    docFileCreateRequest.state = DocumentFileCreateRequestState.FileChoosen;
    docFileCreateRequest.companyId = this.company.id;
    docFileCreateRequest.originalDocumentName = uploadedFile.fileItem.file.name;
    docFileCreateRequest.documentType = uploadedFile.selectedType;
    docFileCreateRequest.customAttributes = uploadedFile.customAttributesValues;
    docFileCreateRequest.statementDate = uploadedFile.selectedStatementDate !== '' ? uploadedFile.selectedStatementDate : undefined;
    docFileCreateRequest.taxFormType = uploadedFile.selectedTaxForm;
    docFileCreateRequest.currency = this.company.settings['currency'];
    docFileCreateRequest.uploader = this.uploader;
    docFileCreateRequest.additionalInformation = '';
    docFileCreateRequest.requestedPeriods = 'all';
    docFileCreateRequest.fileItem = uploadedFile.fileItem;
    return docFileCreateRequest
  }

  _buildCreateFilePayload(createFileRequest) {
    // clear unused/problematic circular reference from payload in multi-upload scenario
    return {
      ...createFileRequest,
      uploader: null,
      fileItem: {...createFileRequest.fileItem, uploader: null}
    }
  }

  updateSupportedForms() {
    this.SUPPORTED_TAX_FORMS = Array.from(SUPPORTED_TAX_FORMS);
    this.SUPPORTED_TAX_FORMS.push('1040 Schedule C');
    if (this.showPuertoRicoCorporate) {
      this.SUPPORTED_TAX_FORMS.push('Puerto Rico Corporate')
    }
    if (this.showPuertoRicoIndividual) {
      this.SUPPORTED_TAX_FORMS.push('Puerto Rico Individual')
    }
    this.SUPPORTED_TAX_FORMS.push('Schedule E')
    this.SUPPORTED_TAX_FORMS.push('Schedule F')

    this.taxFormOptions = this.SUPPORTED_TAX_FORMS.map(form => {
      return {
        id: form,
        text: form
      }
    })
  }

  validateForm() {
    if (!this.uploadedFiles?.length) {
      this.fileNotSelectedError = true;
      return false;
    }
    let formValid = true;

    for (let uploadedFile of this.uploadedFiles) {
      if (!uploadedFile.selectedType || uploadedFile.selectedType.length === 0) {
        uploadedFile.error.selectedType = true;
        formValid = false;
      }
      if (uploadedFile.selectedType === TAX_RETURN) {
        if (!uploadedFile.selectedTaxForm) {
          uploadedFile.error.selectedTaxForm = true;
          formValid = false;
        }
        if (!uploadedFile.selectedStatementDate) {
          uploadedFile.error.selectedStatementDate = true;
          formValid = false;
        }
      }
      this.customAttrDefinitions.forEach((attrDefinition) => {
        if (attrDefinition.required) {
          if (!uploadedFile.customAttributesValues.hasOwnProperty(attrDefinition.key) ||
            !uploadedFile.customAttributesValues[attrDefinition.key]) {
            // mark input error
            uploadedFile.error.invalidCustomAttributeFields[attrDefinition.key] = true;
            formValid = false;
          }
        }
      });
    }
    return formValid;
  }

  updateFlags(): void {
    this.showPuertoRicoCorporate = this._featureFlags.flags['puerto-rico-corporate'];
    this.showPuertoRicoIndividual = this._featureFlags.flags['puerto-rico-individual'];
    this.showCombineDocumentFeature = this._featureFlags.flags['combine-document-upload'];
  }

  close(): void {
    this.popup.close();
  }

  uploadAnother(): void {
    this.popup.close(true, {reOpenModal: true});
  }

  navigateToDocumentsTab(): void {
    this._router.navigate(['/companies/', this.company.uuid, 'data']);
    this.close();
  }

  scrollToBottomOfDocumentsList(){
    const docRowElements = this.uploadedDocumentRows.toArray();
    if (docRowElements.length < 2){
      return;
    }
    const lastElement = docRowElements[docRowElements.length - 1];
    lastElement.nativeElement.scrollIntoView({behavior: 'smooth'});
  }

  scrollToCombinitionSummary(){
    setTimeout(() => {
      const element = document.getElementById('combinedFileName');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }
    }, 100);
  }
}
